import { FC, useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Plus, Trash2, Upload, ChevronDown } from 'lucide-react';
import FormFieldCalendar from '@/components/FormFieldCalendar';
import { FormFieldTimePicker } from '@/components/FormFieldTimePicker';
import FormFieldComponent from '@/components/FormFieldComponent';
import { ImageDisplayDialog, ImageType } from '@/pages/caseManagement/processDetails/ImageDisplayDialog';
import { DeliveryUpdateBillingPreview } from './DeliveryUpdateBillingPreview';
import type { DeliveryUpdateFormData } from './deliveryUpdate.schema';
import {
  ALLOWED_DELIVERY_UPDATE_IMAGE_EXTENSIONS,
  ALLOWED_DELIVERY_UPDATE_IMAGE_MIME_TYPES,
  isAllowedDeliveryUpdateImageFile,
  MAX_DELIVERY_UPDATE_IMAGE_SIZE_BYTES,
  MAX_DELIVERY_UPDATE_IMAGES,
} from './deliveryUpdate.schema';
import { genUniqueId } from '@/lib/utils';
import FormFieldCheckbox from '@/components/FormFieldCheckbox';
import type { DeliveryDetailResponse } from '@/types/deliveryDetail.types';
import { isWorkOrderDetail } from '@/types/deliveryDetail.types';
import { uploadMedia } from '@/services/uploadMedia';
import { toast } from '@/components/ui/use-toast';

interface DeliveryUpdateFormProps {
  isReadOnly?: boolean;
  isAddMode?: boolean;
  workOrderData?: DeliveryDetailResponse | null;
  onMediaUploadingChange?: (uploading: boolean) => void;
}

const FEE_TYPES = ['Tolls', 'Parking Fee', 'Filing Fee', 'Other'] as const;
type FeeType = (typeof FEE_TYPES)[number];
type DeliveryUpdateImage = DeliveryUpdateFormData['images'][number];

const toImageType = (image: DeliveryUpdateImage): ImageType => ({
  id: image.id ?? image.mediaId ?? '',
  name: image.name ?? 'Image',
  image: image.image instanceof File ? image.image : null,
  mediaId: image.mediaId,
  mediaData: image.mediaData as ImageType['mediaData'],
});

export const DeliveryUpdateForm: FC<DeliveryUpdateFormProps> = ({
  isReadOnly = false,
  isAddMode = false,
  workOrderData,
  onMediaUploadingChange,
}) => {
  const { control, watch, setValue, getValues } = useFormContext<DeliveryUpdateFormData>();
  const [displayPreview, setDisplayPreview] = useState(false);
  const [previewImage, setPreviewImage] = useState<ImageType | null>(null);

  const images = watch('images') || [];
  const additionalFees = watch('additionalFees') || [];
  const hasReturn = watch('hasReturn') || false;
  const waitingTime = watch('waitingTime');
  const completedDate = watch('completedDate');

  useEffect(() => {
    if (isReadOnly || !isAddMode || !(completedDate instanceof Date)) return;

    setValue('date', completedDate, { shouldValidate: true });
  }, [completedDate, isReadOnly, isAddMode, setValue]);

  const imageAcceptList = [
    ...ALLOWED_DELIVERY_UPDATE_IMAGE_MIME_TYPES,
    ...ALLOWED_DELIVERY_UPDATE_IMAGE_EXTENSIONS.map((ext) => `.${ext}`),
  ].join(',');

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const fileList = Array.from(files);
    const currentImages = getValues('images') || [];

    if (currentImages.length + fileList.length > MAX_DELIVERY_UPDATE_IMAGES) {
      toast({
        variant: 'destructive',
        description: `Maximum ${MAX_DELIVERY_UPDATE_IMAGES} images allowed.`,
        duration: 5000,
      });
      event.target.value = '';
      return;
    }

    for (const file of fileList) {
      if (!isAllowedDeliveryUpdateImageFile(file)) {
        toast({
          variant: 'destructive',
          description: `${file.name}: unsupported file type. Allowed: JPEG, JPG, PNG, HEIF, HEIC, RAW.`,
          duration: 5000,
        });
        event.target.value = '';
        return;
      }
      if (file.size > MAX_DELIVERY_UPDATE_IMAGE_SIZE_BYTES) {
        toast({
          variant: 'destructive',
          description: `${file.name}: file size exceeds 20 MB limit.`,
          duration: 5000,
        });
        event.target.value = '';
        return;
      }
    }

    onMediaUploadingChange?.(true);
    try {
      const uploadedImages: DeliveryUpdateFormData['images'] = [];

      for (const file of fileList) {
        const uploaded = await uploadMedia(file, 'DELIVERY_UPDATE');
        if (!uploaded?.mediaId) {
          throw new Error(`Failed to upload ${file.name}`);
        }

        uploadedImages.push({
          id: genUniqueId(),
          name: file.name,
          mediaId: uploaded.mediaId,
        });
      }

      setValue('images', [...currentImages, ...uploadedImages], { shouldValidate: true });
    } catch {
      toast({
        variant: 'destructive',
        description: 'Failed to upload one or more images. Please try again.',
        duration: 5000,
      });
    } finally {
      onMediaUploadingChange?.(false);
      event.target.value = '';
    }
  };

  const handleImageDelete = (index: number) => {
    const updatedImages = images.filter((_, i) => i !== index);
    setValue('images', updatedImages, { shouldValidate: true });
  };

  const handleImageClick = (image: DeliveryUpdateImage) => {
    setPreviewImage(toImageType(image));
    setDisplayPreview(true);
  };

  const handleAddFee = (feeType: FeeType) => {
    setValue('additionalFees', [...additionalFees, { feeType, feeAmount: 0.01 }], { shouldValidate: false });
  };

  const handleRemoveFee = (index: number) => {
    const updatedFees = additionalFees.filter((_, i) => i !== index);
    setValue('additionalFees', updatedFees, { shouldValidate: true });
  };

  const handleFeeAmountChange = (index: number, value: string) => {
    // Get fresh values from form state
    const currentFees = getValues('additionalFees') || [];
    const updatedFees = [...currentFees];

    if (value === '') {
      updatedFees[index] = { ...updatedFees[index], feeAmount: 0 };
      setValue('additionalFees', updatedFees, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
      return;
    }

    const numValue = parseFloat(value);
    // Only update if it's a valid number
    if (!isNaN(numValue) && numValue >= 0) {
      updatedFees[index] = { ...updatedFees[index], feeAmount: numValue };
      setValue('additionalFees', updatedFees, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    }
  };

  const disabledStyle = isReadOnly ? 'disabled:opacity-100 disabled:cursor-not-allowed' : '';

  return (
    <>
      <div className="space-y-8">
        {/* Delivery Completion Details Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-primary">Delivery Completion Details</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormFieldCalendar
              name="completedDate"
              label="Completed Date"
              placeholder="Select date"
              control={control}
              required
              disabled={isReadOnly}
              disabledAt={(date) => {
                const normalized = new Date(date);
                normalized.setHours(0, 0, 0, 0);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return normalized.getTime() > today.getTime();
              }}
              className={disabledStyle}
            />

            <FormFieldTimePicker
              name="completedTime"
              label="Completed Time"
              placeHolder="Select time"
              required
              disabled={isReadOnly}
              className={disabledStyle}
              timeFields={['H', 'M']}
              use12HourFormat={true}
            />
          </div>

        </div>

        {/* Recipient & Waiting Time Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-primary">Recipient &amp; Waiting Time</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormFieldComponent
              name="receivedBy"
              label="Received By"
              placeholder="Enter name"
              control={control}
              required
              disabled={isReadOnly}
              className={disabledStyle}
            />

            <FormFieldCalendar
              name="date"
              label="Date"
              placeholder="Select date"
              control={control}
              required
              disabled={isReadOnly}
              disabledAt={(date) => {
                const normalized = new Date(date);
                normalized.setHours(0, 0, 0, 0);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return normalized.getTime() > today.getTime();
              }}
              className={disabledStyle}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormFieldTimePicker
              name="waitingTime"
              label="Waiting Time"
              placeHolder="Select waiting time"
              disabled={isReadOnly}
              className={disabledStyle}
              timeFields={['H', 'M']}
              use12HourFormat={false}
            />
          </div>
        </div>

        {/* Return Details Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-primary">Return Details</h3>

          <FormFieldCheckbox
            name="hasReturn"
            label="Return"
            control={control}
            disabled={isReadOnly}
          />

          {hasReturn && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormFieldComponent
                name="returnName"
                label="Return Name"
                placeholder="Enter return name"
                control={control}
                required
                disabled={isReadOnly}
                className={disabledStyle}
              />

              <FormFieldCalendar
                name="returnDate"
                label="Return Date"
                placeholder="Select return date"
                control={control}
                required
                disabled={isReadOnly}
                className={disabledStyle}
              />
            </div>
          )}
        </div>

        {/* Images Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-primary">Uploaded Images</h3>

          {!isReadOnly && (
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-gray-400 transition-colors"
              onClick={() => document.getElementById('image-upload')?.click()}
            >
              <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
              <p className="text-sm text-gray-600">Click to upload image</p>
              <p className="text-xs text-gray-500 mt-1">JPEG, JPG, PNG, HEIF, HEIC, RAW (Max 20MB per image, 50 images total)</p>
            </div>
          )}

          <input
            id="image-upload"
            type="file"
            accept={imageAcceptList}
            multiple
            className="hidden"
            onChange={(event) => {
              void handleImageUpload(event);
            }}
            disabled={isReadOnly || images.length >= MAX_DELIVERY_UPDATE_IMAGES}
            aria-label="Image file input"
          />

          {images.length > 0 && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4">
                {images.map((image, index) => (
                  <div
                    key={image.id ?? index}
                    className="border rounded-lg p-3 hover:bg-gray-50 cursor-pointer flex items-center justify-between gap-2"
                    onClick={() => handleImageClick(image)}
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <Upload className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      <span className="text-sm truncate">
                        {image.name ?? `Image ${index + 1}`}
                      </span>
                    </div>
                    {!isReadOnly && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleImageDelete(index);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-sm text-muted-foreground text-right">
                {images.length} of {MAX_DELIVERY_UPDATE_IMAGES}
              </p>
            </>
          )}
        </div>

        {/* Additional Fees Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-primary">Additional Fees</h3>
            {!isReadOnly && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="outline" size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Additional Fees
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {FEE_TYPES.map((type) => (
                    <DropdownMenuItem key={type} onClick={() => handleAddFee(type)}>
                      {type}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {additionalFees.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground">No additional fees</div>
          ) : (
            <div className="space-y-4">
              {additionalFees.map((fee, index) => (
                <div key={fee.feeId ?? `fee-${index}-${fee.feeType}`} className="flex gap-3 items-start border rounded-lg p-3">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-medium">Fee Type</label>
                      <input
                        type="text"
                        value={fee.feeType}
                        disabled
                        className="w-full px-3 py-2 border rounded-md bg-gray-50"
                      />
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-medium">
                        Amount <span className="text-destructive">*</span>
                      </label>
                      <input
                        type="number"
                        value={fee.feeAmount || ''}
                        onChange={(e) => handleFeeAmountChange(index, e.target.value)}
                        disabled={isReadOnly}
                        min="0"
                        step="1.00"
                        className="w-full px-3 py-2 border rounded-md disabled:opacity-50"
                      />
                    </div>
                  </div>

                  {!isReadOnly && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveFee(index)}
                      className="mt-6"
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Billing Preview - Only show in add/edit mode */}
        {!isReadOnly && (
          <DeliveryUpdateBillingPreview
            waitingTime={waitingTime}
            additionalFees={additionalFees}
            waitingTimeHourlyRate={
              workOrderData && isWorkOrderDetail(workOrderData)
                ? workOrderData.deliveryUpdates.waitingTimeHourlyRate
                : undefined
            }
          />
        )}
      </div >

      {/* Image Preview Modal */}
      {
        displayPreview && previewImage && (
          <ImageDisplayDialog
            attachmentId={previewImage.id}
            attachments={images.map(toImageType)}
            onClose={() => setDisplayPreview(false)}
          />
        )
      }
    </>
  );
};
